import React, { useMemo } from 'react';
import { Select } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { LabeledValue } from 'antd/lib/select';
import clsx from 'clsx';

import { IUserCertificate, ItemTypeEnum } from '@share/common-types';
import { filterCertificateItemsByTypeAndStatus } from '@share/utils';
import styles from './styles.module.scss';

export interface ICertificateSelectChange {
  certificateId: string;
  certificateItemId: string;
}

interface CertificateSelectProps {
  certificates: IUserCertificate[];
  value: string;
  onChange: (selection: ICertificateSelectChange | null) => void;
  disabled?: boolean;
  className?: string;
  type?: ItemTypeEnum.Condo | ItemTypeEnum.Hotel;
  /** When false, hides the None option (public ClubSite certificate holders). */
  allowNoneOption?: boolean;
}

export const CertificateSelect: React.FC<CertificateSelectProps> = ({
  certificates = [],
  value,
  onChange,
  disabled = false,
  className,
  type = ItemTypeEnum.Condo,
  allowNoneOption = true,
}) => {
  const intl = useIntl();
  const filteredCertificates = useMemo((): IUserCertificate[] => {
    return certificates.filter((certificate) => {
      return filterCertificateItemsByTypeAndStatus(certificate.items, type).length > 0;
    });
  }, [certificates, type]);

  const options: LabeledValue[] = useMemo(() => {
    const certificateOptions = filteredCertificates.map((certificate) => {
      const item = filterCertificateItemsByTypeAndStatus(certificate.items, type)[0];
      const nights = item?.nights ?? 0;
      const nightsLabel = intl.formatMessage({ id: 'select.certificate.nights' }, { nights });

      return {
        value: certificate.id,
        label: (
          <span className={styles.certificateOption}>
            <span className={styles.certificateOptionCode}>{certificate.code}</span>
            <span className={styles.certificateOptionNights}>{nightsLabel}</span>
          </span>
        ),
      };
    });

    if (!allowNoneOption) {
      return certificateOptions;
    }

    return [
      { value: '', label: intl.formatMessage({ id: 'select.certificate.none' }) },
      ...certificateOptions,
    ];
  }, [allowNoneOption, filteredCertificates, intl, type]);

  if (!filteredCertificates.length) {
    return null;
  }

  return (
    <div className={clsx(styles.certificateSelect, className)}>
      <p className={styles.certificateSelectLabel}>
        <FormattedMessage id="select.certificate.label" />
      </p>
      <Select
        options={options}
        value={value ?? ''}
        disabled={disabled}
        onChange={(selectedValue: string) => {
          if (!selectedValue) {
            if (allowNoneOption) {
              onChange(null);
            }
            return;
          }
          const cert = filteredCertificates.find((c) => c.id === selectedValue);
          const item = filterCertificateItemsByTypeAndStatus(cert?.items, type)[0];
          onChange({
            certificateId: selectedValue,
            certificateItemId: item?.id ?? '',
          });
        }}
        placeholder={intl.formatMessage({ id: 'select.certificate.placeholder' })}
        className={styles.certificateSelectSelect}
      />
    </div>
  );
};
